import type WebSocket from 'ws';
import type { AcpManager } from '../agents/acp/create-acp-manager.js';
import type { CliE2eeRuntime } from '../lib/e2ee/index.js';
import type { PreviewEnvironmentManager } from '../preview-environments/preview-environment-manager.js';
import type { LogFn } from '../log.js';
import type { MessageHandlerDeps } from '../routing/message-handler-deps.js';
import type { PreviewWorktreeManager } from '../worktrees/manager/preview-worktree-manager.js';
import type { SessionWorktreeManager } from '../worktrees/manager.js';
import { updateFirehoseIdentifyOnOpenConnection } from './attach-firehose-after-identified.js';
import type { BridgeIdentifiedPayload } from './bridge-identified-types.js';
import { createWarmupAgentCapabilitiesOnConnect } from './create-bridge-agent-capabilities-reporter.js';
import { createBridgeMessageDeps } from './create-bridge-message-deps.js';
import { createReportAutoDetectedAgents, createSendLocalSkillsReport } from './create-bridge-local-reports.js';
import type { BridgeConnectionTokens } from './bridge-connection-runtime-types.js';
import type { BridgeHeartbeatController } from './heartbeat/controller.js';
import type { MainBridgeConnectionState } from './create-main-bridge-reconnect-state.js';

export function createBridgeRuntimeMessageDeps(params: {
  apiUrl: string;
  workspaceId: string;
  state: MainBridgeConnectionState;
  getWs: () => WebSocket | null;
  log: LogFn;
  tokens: BridgeConnectionTokens;
  acpManager: AcpManager;
  sessionWorktreeManager: SessionWorktreeManager;
  previewWorktreeManager: PreviewWorktreeManager;
  previewEnvironmentManager: PreviewEnvironmentManager;
  e2ee: CliE2eeRuntime | undefined;
  onBridgeIdentified: (msg: BridgeIdentifiedPayload) => void;
  bridgeHeartbeat: BridgeHeartbeatController;
}): MessageHandlerDeps {
  const {
    apiUrl,
    workspaceId,
    state,
    getWs,
    log,
    tokens,
    acpManager,
    sessionWorktreeManager,
    previewWorktreeManager,
    previewEnvironmentManager,
    e2ee,
    onBridgeIdentified,
    bridgeHeartbeat,
  } = params;

  return createBridgeMessageDeps({
    getWs,
    log,
    acpManager,
    sessionWorktreeManager,
    previewWorktreeManager,
    onBridgeIdentified,
    bridgeHeartbeat,
    sendLocalSkillsReport: createSendLocalSkillsReport(getWs, log),
    reportAutoDetectedAgents: createReportAutoDetectedAgents(getWs, log),
    warmupAgentCapabilitiesOnConnect: createWarmupAgentCapabilitiesOnConnect({
      workspaceId,
      log,
      getWs,
    }),
    previewEnvironmentManager,
    updateFirehoseProxyPorts: (proxyPorts) => {
      const p = state.lastFirehoseParams;
      if (!p) return;
      updateFirehoseIdentifyOnOpenConnection(state, { ...p, proxyPorts });
    },
    e2ee,
    cloudApiBaseUrl: apiUrl,
    getCloudAccessToken: () => tokens.accessToken,
  });
}
